/**
 * SVG 导入相关工具函数
 * 负责清洗、校验和提取导入的 SVG 内容
 */

/** SVG 提取结果 */
export interface SvgExtractResult {
  /** SVG 内层内容（不含外层 <svg> 标签） */
  innerContent: string;
  /** 原始完整 SVG（含 <svg> 标签，用于保存 originalSvgContent） */
  fullSvgContent: string;
  /** 提取到的宽度 */
  width: number;
  /** 提取到的高度 */
  height: number;
  /** viewBox 字符串（如果存在） */
  viewBox?: string;
}

/**
 * 校验字符串是否包含有效的 SVG 内容
 * 支持：完整 <svg>...</svg>、纯 SVG 子元素（如 <path>、<circle> 等）
 *
 * AI 生成的 SVG 常含畸形属性（标签名和属性粘连、属性间缺空格等），
 * 严格 XML 解析会失败，因此先尝试 XML，失败后回退到 HTML 容错解析。
 */
export const validateSvgContent = (content: string): { valid: boolean; message: string } => {
  const trimmed = content.trim();

  if (!trimmed) {
    return { valid: false, message: '内容为空' };
  }

  const parser = new DOMParser();

  // 情况1：完整的 <svg> 标签
  if (trimmed.startsWith('<svg') || trimmed.startsWith('<?xml')) {
    // 先严格 XML
    const xmlDoc = parser.parseFromString(trimmed, 'image/svg+xml');
    if (!xmlDoc.querySelector('parsererror')) {
      if (xmlDoc.querySelector('svg')) {
        return { valid: true, message: '' };
      }
      return { valid: false, message: '未找到有效的 <svg> 标签' };
    }
    // 回退到 HTML 容错
    const htmlDoc = parser.parseFromString(trimmed, 'text/html');
    const svgEl = htmlDoc.querySelector('svg');
    if (svgEl && svgEl.children.length > 0) {
      return { valid: true, message: '' };
    }
    return { valid: false, message: '未找到有效的 <svg> 标签' };
  }

  // 情况2：纯 SVG 子元素（包裹在临时 <svg> 中解析）
  const wrapped = `<svg xmlns="http://www.w3.org/2000/svg">${trimmed}</svg>`;
  const xmlDoc = parser.parseFromString(wrapped, 'image/svg+xml');
  let rootEl: Element;
  if (!xmlDoc.querySelector('parsererror')) {
    rootEl = xmlDoc.documentElement;
  } else {
    const htmlDoc = parser.parseFromString(wrapped, 'text/html');
    const svgEl = htmlDoc.querySelector('svg');
    if (!svgEl) {
      return { valid: false, message: 'SVG 内容解析失败' };
    }
    rootEl = svgEl;
  }

  // 检查是否包含至少一个有效的 SVG 子元素
  const SVG_CHILD_TAGS = ['path', 'circle', 'rect', 'line', 'ellipse', 'polygon', 'polyline', 'g', 'text', 'image', 'use', 'defs'];
  const hasValidChild = SVG_CHILD_TAGS.some(tag => rootEl.querySelector(tag) !== null);
  if (!hasValidChild) {
    return { valid: false, message: '未找到有效的 SVG 图形元素（如 path, circle, rect 等）' };
  }

  return { valid: true, message: '' };
};

/**
 * 从 viewBox 字符串中提取宽高
 * 例如 "0 0 100 200" -> { width: 100, height: 200 }
 */
const parseViewBox = (viewBox: string): { width: number; height: number } | null => {
  const parts = viewBox.trim().split(/[\s,]+/);
  if (parts.length === 4) {
    const w = parseFloat(parts[2]);
    const h = parseFloat(parts[3]);
    if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
      return { width: w, height: h };
    }
  }
  return null;
};

/**
 * 解析 SVG 的 width/height 属性为像素数值
 * 
 * 只接受纯数字或 px 单位，忽略百分比/em/rem 等相对单位
 * 例如:
 *  - "200"    → 200
 *  - "200px"  → 200
 *  - "100%"   → null（忽略）
 *  - "2em"    → null（忽略）
 *  - ""       → null
 */
const parseAbsoluteLength = (value: string | null): number | null => {
  if (!value) return null;
  const trimmed = value.trim();
  // 只匹配纯数字或以 px 结尾的数字
  const match = trimmed.match(/^([0-9]*\.?[0-9]+)\s*(px)?$/i);
  if (!match) return null;
  const num = parseFloat(match[1]);
  return (!isNaN(num) && num > 0) ? num : null;
};

/**
 * 从 SVG 字符串中提取内层内容和尺寸信息
 *
 * 处理两种输入：
 * 1. 完整 <svg>...</svg> — 提取 innerHTML、width/height/viewBox
 * 2. 纯子元素片段 — 直接作为 innerHTML，使用默认尺寸
 * 
 * 尺寸提取优先级：
 * - 有 viewBox → 以 viewBox 为准（这是 SVG 的真实坐标系）
 * - 无 viewBox → 取 width/height 属性（仅纯数字或 px 单位）
 * - 都没有 → 使用默认尺寸 200x200
 */
export const extractSvgContent = (raw: string): SvgExtractResult => {
  const trimmed = raw.trim();
  const parser = new DOMParser();

  const DEFAULT_SIZE = 200;

  // 情况1：完整 <svg> 标签
  if (trimmed.startsWith('<svg') || trimmed.startsWith('<?xml')) {
    // 优先严格 XML，失败则回退 HTML 容错
    let svgEl: Element | null = null;
    const xmlDoc = parser.parseFromString(trimmed, 'image/svg+xml');
    if (!xmlDoc.querySelector('parsererror')) {
      svgEl = xmlDoc.querySelector('svg');
    } else {
      const htmlDoc = parser.parseFromString(trimmed, 'text/html');
      svgEl = htmlDoc.querySelector('svg');
    }

    if (svgEl) {
      const viewBoxAttr = svgEl.getAttribute('viewBox') ?? undefined;
      const widthAttr = svgEl.getAttribute('width');
      const heightAttr = svgEl.getAttribute('height');

      let width = DEFAULT_SIZE;
      let height = DEFAULT_SIZE;

      // 优先级1：viewBox（最可靠的坐标系定义）
      const vb = viewBoxAttr ? parseViewBox(viewBoxAttr) : null;
      if (vb) {
        width = vb.width;
        height = vb.height;
      } else {
        // 优先级2：仅在没有 viewBox 时，才用 width/height 属性（且必须是绝对单位）
        const parsedW = parseAbsoluteLength(widthAttr);
        const parsedH = parseAbsoluteLength(heightAttr);
        if (parsedW) width = parsedW;
        if (parsedH) height = parsedH;
      }

      const innerContent = svgEl.innerHTML;

      return {
        innerContent,
        fullSvgContent: innerContent,
        width,
        height,
        viewBox: viewBoxAttr,
      };
    }
  }

  // 情况2：纯子元素片段
  return {
    innerContent: trimmed,
    fullSvgContent: trimmed,
    width: DEFAULT_SIZE,
    height: DEFAULT_SIZE,
  };
};

/**
 * 读取文件内容为文本
 */
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('读取文件失败：结果不是文本'));
      }
    };
    reader.onerror = () => reject(new Error(`读取文件失败: ${reader.error?.message ?? '未知错误'}`));
    reader.readAsText(file);
  });
};